import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Column } from "primereact/column";
import { DataTable } from "primereact/datatable";

import TextWithLabel from "../../../controls/TextWithLabel";
import Button from "../../../ui/Button";

function canPromoteReceivingItem(item) {
  if (!item?.id) {
    return false;
  }
  const inStock = parseFloat(String(item.in_stock ?? "").replace(/,/g, ""));
  return Number.isFinite(inStock) && inStock > 0;
}

function resolveSupplierName(record) {
  if (!record) return null;
  const raw = record.supplier_name ?? record.supplier;
  if (typeof raw === "string" && raw.trim()) {
    return raw;
  }
  if (raw && typeof raw === "object" && raw.name) {
    return String(raw.name);
  }
  return null;
}

function displaySupplierName(record, fallback = "N/A") {
  return resolveSupplierName(record) ?? fallback;
}

function resolveLocationName(record) {
  if (!record?.location) return null;
  const location = record.location;
  if (typeof location === "string" && location.trim()) {
    return location;
  }
  if (location && typeof location === "object") {
    return location.name ?? location.label ?? null;
  }
  return null;
}

function displayLocationName(record, fallback = "N/A") {
  return resolveLocationName(record) ?? fallback;
}

function formatDateTime(value) {
  if (!value) return "N/A";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return {
      dateText: value,
      timeText: "",
    };
  }

  return {
    dateText: date.toLocaleDateString(undefined, {
      year: "numeric",
      month: "short",
      day: "numeric",
    }),
    timeText: date.toLocaleTimeString(undefined, {
      hour: "numeric",
      minute: "2-digit",
      second: "2-digit",
    }),
  };
}

function formatBarcodeSerial(itemOrBarcode) {
  if (itemOrBarcode && typeof itemOrBarcode === "object") {
    if (itemOrBarcode.barcode_serial) {
      return String(itemOrBarcode.barcode_serial);
    }
    return formatBarcodeSerial(itemOrBarcode.barcode);
  }

  if (!itemOrBarcode) return "N/A";
  const barcode = String(itemOrBarcode);
  if (barcode.length <= 15) return barcode;
  return `...${barcode.slice(-15)}`;
}

function historyAmount(event) {
  return event.total_unit ?? event.received_amount;
}

function getProductionContext(event) {
  return (
    event.context?.production ??
    event.context?.manufactured_product ??
    event.context?.manufacturing ??
    null
  );
}

function getPackagingContext(event) {
  return event.context?.packaging ?? null;
}

function getManufacturedProductId(event) {
  const fromMeta = event.metadata?.manufactured_product_id;
  if (fromMeta != null) return fromMeta;
  const ctx = getProductionContext(event);
  if (ctx && typeof ctx === "object" && ctx.id != null) return ctx.id;
  return null;
}

function getPackagingRecordId(event) {
  const fromMeta = event.metadata?.packaging_record_id;
  if (fromMeta != null) return fromMeta;
  const ctx = getPackagingContext(event);
  if (ctx && typeof ctx === "object" && ctx.id != null) return ctx.id;
  return null;
}

function productionDisplayName(ctx) {
  if (!ctx || typeof ctx !== "object") return null;
  return ctx.product_name ?? ctx.name ?? null;
}

function getActionBadgeClass(action) {
  const text = (action || "").toLowerCase();
  if (text.includes("promoted")) {
    return "bg-indigo-100 text-indigo-700";
  }
  if (text.includes("deletion") || text.includes("reversal")) {
    return "bg-red-100 text-[#DC2626]";
  }
  if (text.includes("creation")) {
    return "bg-green-100 text-green-700";
  }
  if (text.includes("update") || text.includes("adjustment")) {
    return "bg-amber-100 text-[#B45309]";
  }
  return "bg-[#EFF6FF] text-[#0F172A]";
}

function formatAmountWithUom(value, unitOfMeasure) {
  if (value == null) return "N/A";
  const uom = unitOfMeasure ? ` ${unitOfMeasure}` : "";
  return `${value}${uom}`;
}

function historyRowKey(event, index) {
  if (event.id != null) return String(event.id);
  const barcode = event.barcode != null ? String(event.barcode) : "na";
  const created = event.created_at != null ? String(event.created_at) : "";
  return `${barcode}-${created}-${index}`;
}

function contextKeysBeyondInvoice(context) {
  if (!context || typeof context !== "object") return [];
  return Object.keys(context).filter((k) => k !== "invoice" && context[k] != null);
}

function hasExpandableHistoryDetail(event) {
  if (event.context?.invoice) return true;
  if (event.metadata && Object.keys(event.metadata).length > 0) return true;
  if (getManufacturedProductId(event) != null) return true;
  if (getPackagingRecordId(event) != null) return true;
  if (contextKeysBeyondInvoice(event.context).length > 0) return true;
  return false;
}

function HistoryInvoiceFullPanel({ invoice: contextInvoice }) {
  if (!contextInvoice) return null;
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Invoice Context
        </p>
        {contextInvoice.id ? (
          <Link
            className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
            to={`/receiving-invoices/${contextInvoice.id}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Link>
        ) : null}
      </div>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TextWithLabel
          label="Invoice Number"
          text={contextInvoice.invoice_number || "N/A"}
        />
        <TextWithLabel
          label="Supplier Name"
          text={displaySupplierName(contextInvoice)}
        />
        <TextWithLabel label="Date" text={contextInvoice.date || "N/A"} />
        <TextWithLabel
          label="Location"
          text={displayLocationName(contextInvoice)}
        />
      </div>
    </div>
  );
}

function HistoryManufacturingPanel({ event }) {
  const productId = getManufacturedProductId(event);
  const ctxProduct = getProductionContext(event);
  const meta = event.metadata;

  if (productId == null && !ctxProduct && !meta?.manufactured_product_ingredient_id) {
    return null;
  }

  const displayName = productionDisplayName(ctxProduct);

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Production / manufacturing
        </p>
        {productId != null ? (
          <Link
            className="rounded-md bg-main-purple px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
            to={`/manufactured-products/${productId}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            View
          </Link>
        ) : null}
      </div>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {productId != null ? (
          <TextWithLabel label="Manufactured product ID" text={String(productId)} />
        ) : null}
        {displayName ? <TextWithLabel label="Name" text={displayName} /> : null}
        {ctxProduct?.lot_number != null ? (
          <TextWithLabel label="Lot" text={String(ctxProduct.lot_number)} />
        ) : null}
        {ctxProduct?.plu != null ? (
          <TextWithLabel label="PLU" text={String(ctxProduct.plu)} />
        ) : null}
        {meta?.manufactured_product_ingredient_id != null ? (
          <TextWithLabel
            label="Ingredient link ID"
            text={String(meta.manufactured_product_ingredient_id)}
          />
        ) : null}
        {meta?.flow != null ? <TextWithLabel label="Flow" text={String(meta.flow)} /> : null}
        {meta?.delta_used_weight != null ? (
          <TextWithLabel label="Delta used weight" text={String(meta.delta_used_weight)} />
        ) : null}
        {meta?.delta_in_stock != null ? (
          <TextWithLabel label="Delta in stock" text={String(meta.delta_in_stock)} />
        ) : null}
      </div>
    </div>
  );
}

function HistoryPackagingPanel({ event }) {
  const packagingId = getPackagingRecordId(event);
  const packaging = getPackagingContext(event);
  const meta = event.metadata;

  if (packagingId == null && !packaging) {
    return null;
  }

  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
      <div className="flex items-center justify-between gap-2">
        <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
          Packaging / promote
        </p>
      </div>
      <div className="mt-2 grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {packagingId != null ? (
          <TextWithLabel label="Packaging record ID" text={String(packagingId)} />
        ) : null}
        {packaging?.product_name ? (
          <TextWithLabel label="Product" text={String(packaging.product_name)} />
        ) : null}
        {packaging?.plu ? (
          <TextWithLabel label="PLU" text={String(packaging.plu)} />
        ) : null}
        {packaging?.lot_number ? (
          <TextWithLabel label="Lot" text={String(packaging.lot_number)} />
        ) : null}
        {meta?.promoted_quantity != null ? (
          <TextWithLabel
            label="Promoted quantity"
            text={String(meta.promoted_quantity)}
          />
        ) : null}
      </div>
    </div>
  );
}

// function HistoryOtherContextPanel({ event }) {
//   const keys = contextKeysBeyondInvoice(event.context).filter((k) => {
//     const v = event.context[k];
//     if (v === event.context?.invoice) return false;
//     if (k === "manufactured_product" || k === "manufacturing") return false;
//     return true;
//   });
//   if (keys.length === 0) return null;

//   return (
//     <div className="rounded-lg border border-gray-200 bg-white p-3">
//       <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
//         Additional context
//       </p>
//       <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
//         {JSON.stringify(
//           keys.reduce((acc, k) => {
//             acc[k] = event.context[k];
//             return acc;
//           }, {}),
//           null,
//           2,
//         )}
//       </pre>
//     </div>
//   );
// }

// function HistoryMetadataPanel({ metadata }) {
//   if (!metadata) return null;
//   return (
//     <div className="rounded-lg border border-gray-200 bg-white p-3">
//       <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">Metadata</p>
//       <pre className="mt-2 overflow-auto rounded-md bg-slate-900 p-3 text-xs text-slate-100">
//         {JSON.stringify(metadata, null, 2)}
//       </pre>
//     </div>
//   );
// }

function HistoryEventExpansion({ event }) {
  return (
    <div className="space-y-3 border-t border-gray-200 bg-gray-50/80 p-4">
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <TextWithLabel
          label="Barcode Serial"
          text={formatBarcodeSerial(event.barcode)}
        />
        <TextWithLabel label="Unit Cost" text={event.unit_cost ?? "N/A"} />
      </div>
      <HistoryInvoiceFullPanel invoice={event.context?.invoice} />
      <HistoryManufacturingPanel event={event} />
      <HistoryPackagingPanel event={event} />
      {/* <HistoryOtherContextPanel event={event} /> */}
      {/* <HistoryMetadataPanel metadata={event.metadata} /> */}
    </div>
  );
}

function HistoryContextSummaryCell({ event }) {
  const invoice = event.context?.invoice;
  const productId = getManufacturedProductId(event);
  const ctxProduct = getProductionContext(event);
  const packaging = getPackagingContext(event);
  const packagingId = getPackagingRecordId(event);
  const productionName = productionDisplayName(ctxProduct);
  const extraKeys = contextKeysBeyondInvoice(event.context).filter(
    (k) =>
      k !== "manufactured_product" &&
      k !== "manufacturing" &&
      k !== "production" &&
      k !== "packaging",
  );

  const linkClass =
    "shrink-0 rounded-md bg-main-purple px-2 py-1 text-[11px] font-semibold text-white shadow-sm hover:bg-indigo-600";

  return (
    <div className="flex min-w-[11rem] max-w-md flex-col gap-2 text-xs">
      {invoice ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900">
                {invoice.invoice_number || "—"}
              </p>
              <p
                className="truncate text-gray-600"
                title={resolveSupplierName(invoice) || ""}
              >
                {displaySupplierName(invoice, "—")}
              </p>
            </div>
            {invoice.id ? (
              <Link
                className={linkClass}
                to={`/receiving-invoices/${invoice.id}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Invoice
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {productId != null || ctxProduct ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <p className="font-semibold text-gray-900">Production</p>
              <p
                className="truncate text-gray-600"
                title={productionName || ""}
              >
                {productionName
                  ? String(productionName)
                  : productId != null
                    ? `Product #${productId}`
                    : "—"}
              </p>
            </div>
            {productId != null ? (
              <Link
                className={linkClass}
                to={`/manufactured-products/${productId}`}
                target="_blank"
                rel="noopener noreferrer"
              >
                Open
              </Link>
            ) : null}
          </div>
        </div>
      ) : null}

      {packagingId != null || packaging ? (
        <div className="rounded-md border border-gray-200 bg-gray-50 px-2 py-2">
          <div className="min-w-0 flex-1">
            <p className="font-semibold text-gray-900">Packaging</p>
            <p
              className="truncate text-gray-600"
              title={packaging?.product_name || ""}
            >
              {packaging?.product_name
                ? String(packaging.product_name)
                : packagingId != null
                  ? `Record #${packagingId}`
                  : "—"}
            </p>
          </div>
        </div>
      ) : null}

      {!invoice &&
      productId == null &&
      !ctxProduct &&
      packagingId == null &&
      !packaging &&
      extraKeys.length === 0 ? (
        <span className="text-gray-400">—</span>
      ) : null}
    </div>
  );
}

export default function ReceivingItemLookupResult({ item, invoice, histories, onPromote }) {
  const itemDateTime = formatDateTime(item?.created_at);
  const lastScanDateTime = formatDateTime(histories?.[0]?.created_at);

  const [expandedRows, setExpandedRows] = useState(null);

  const rowsWithKeys = useMemo(
    () =>
      (histories || []).map((event, index) => ({
        ...event,
        _rowKey: historyRowKey(event, index),
      })),
    [histories],
  );

  useEffect(() => {
    setExpandedRows(null);
  }, [item?.id, item?.barcode]);

  const allowExpansion = (rowData) => hasExpandableHistoryDetail(rowData);

  const whenBody = (row) => {
    const dt = formatDateTime(row.created_at);
    return (
      <div>
        <p className="text-xs font-semibold text-gray-800">{dt.dateText}</p>
        {dt.timeText ? <p className="text-xs text-gray-500">{dt.timeText}</p> : null}
      </div>
    );
  };

  const actionBody = (row) => (
    <span
      className={`inline-flex rounded-full px-2 py-0.5 text-xs font-semibold ${getActionBadgeClass(row.action)}`}
    >
      {(row.action || "unknown").replace(/_/g, " ")}
    </span>
  );

  const typeBody = (row) => (
    <span className="inline-flex rounded-full bg-purple-100 px-2 py-0.5 text-xs font-semibold text-purple-700">
      {(row.type || "unknown").replace(/_/g, " ")}
    </span>
  );

  const reasonBody = (row) => {
    const text = row.reason || "—";
    const truncated = text.length > 72 ? `${text.slice(0, 69)}…` : text;
    return (
      <span className="text-gray-700" title={text.length > 72 ? text : undefined}>
        {truncated}
      </span>
    );
  };

  const contextBody = (row) => <HistoryContextSummaryCell event={row} />;

  const rowExpansionTemplate = (rowData) => <HistoryEventExpansion event={rowData} />;

  return (
    <div className="space-y-4 py-4">
      <div className="card-custom rounded-xl border border-gray-200 p-3 sm:p-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
          <h2 className="text-sm font-semibold text-gray-900">Resolved Item</h2>
          {canPromoteReceivingItem(item) ? (
            <Button
              type="button"
              variant="secondary"
              size="sm"
              onClick={() => onPromote?.(item.id)}
            >
              Promote to sellable stock
            </Button>
          ) : null}
        </div>
        <div className="mt-2 grid grid-cols-1 gap-3 lg:grid-cols-12 lg:items-start lg:gap-4">
          <div className="min-w-0 grid grid-cols-2 gap-x-4 gap-y-2 sm:grid-cols-3 lg:col-span-8 xl:grid-cols-4">
            <TextWithLabel
              label="ID"
              text={item?.id ?? "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Barcode Serial"
              text={formatBarcodeSerial(item)}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Product Name"
              text={item?.product_name || "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Lot Number"
              text={item?.lot_number || "N/A"}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Supplier Name"
              text={displaySupplierName(item)}
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Total Unit"
              text={
                item?.received_amount != null
                  ? `${item.received_amount}${item.unit_of_measure ? ` ${item.unit_of_measure}` : ""}`
                  : "N/A"
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="In Stock"
              text={
                item?.in_stock != null
                  ? `${item.in_stock}${item.unit_of_measure ? ` ${item.unit_of_measure}` : ""}`
                  : "N/A"
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Last Scan Date"
              text={
                lastScanDateTime.timeText
                  ? `${lastScanDateTime.dateText}, ${lastScanDateTime.timeText}`
                  : lastScanDateTime.dateText
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
            <TextWithLabel
              label="Created At"
              text={
                itemDateTime.timeText
                  ? `${itemDateTime.dateText}, ${itemDateTime.timeText}`
                  : itemDateTime.dateText
              }
              labelClass="text-[11px] uppercase tracking-wide text-gray-500"
              textClass="text-sm text-gray-900"
            />
          </div>
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-2 sm:p-2.5 lg:col-span-4 lg:h-min">
            <div className="flex items-center justify-between gap-2">
              <p className="text-[11px] font-semibold uppercase tracking-wide text-gray-500">
                Invoice Context
              </p>
              {invoice?.id ? (
                <Link
                  className="rounded-md bg-main-purple px-2.5 py-1 text-xs font-semibold text-white shadow-sm hover:bg-indigo-600"
                  to={`/receiving-invoices/${invoice.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  View
                </Link>
              ) : null}
            </div>
            <div className="mt-1.5 grid grid-cols-2 gap-x-3 gap-y-2">
              <TextWithLabel
                label="Invoice Number"
                text={invoice?.invoice_number || "N/A"}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
              <TextWithLabel
                label="Supplier Name"
                text={displaySupplierName(invoice)}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
              <TextWithLabel
                label="Date"
                text={invoice?.date || "N/A"}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
              <TextWithLabel
                label="Location"
                text={displayLocationName(invoice)}
                labelClass="text-[11px] uppercase tracking-wide text-gray-500"
                textClass="text-sm text-gray-900"
              />
            </div>
          </div>
        </div>
      </div>

      <div className="space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-gray-900">History Timeline</h3>
          <span className="text-sm text-gray-500">
            {histories.length} event{histories.length === 1 ? "" : "s"}
          </span>
        </div>
        {histories.length === 0 ? (
          <div className="card-custom rounded-xl border border-dashed border-gray-300 p-6 text-center text-sm text-gray-500">
            No history rows were returned for this barcode.
          </div>
        ) : (
          <div className="flow-root">
            <div className="-mx-4 -my-2 overflow-x-auto sm:-mx-6 lg:-mx-8">
              <div className="inline-block min-w-full py-2 align-middle sm:px-6 lg:px-8">
                <div className="overflow-hidden card-custom bg-white">
                  <DataTable
                    value={rowsWithKeys}
                    dataKey="_rowKey"
                    expandedRows={expandedRows}
                    onRowToggle={(e) => setExpandedRows(e.data)}
                    rowExpansionTemplate={rowExpansionTemplate}
                    stripedRows
                    tableClassName="min-w-full bg-white divide-y divide-gray-300 text-sm"
                    cellClassName="p-2 border-b border-gray-200 text-sm align-top"
                  >
                    <Column body={whenBody} header="When" style={{ width: "8.5rem" }} />
                    <Column body={actionBody} header="Action" style={{ width: "9rem" }} />
                    <Column body={typeBody} header="Type" style={{ width: "11rem" }} />
                    <Column
                      field="operator.label"
                      header="Operator"
                      body={(r) => r.operator?.label || "—"}
                    />
                    <Column
                      header="Total"
                      body={(r) =>
                        formatAmountWithUom(historyAmount(r), r.unit_of_measure)
                      }
                      style={{ textAlign: "right", width: "6rem" }}
                    />
                    <Column
                      header="In stock"
                      body={(r) => formatAmountWithUom(r.in_stock, r.unit_of_measure)}
                      style={{ textAlign: "right", width: "6rem" }}
                    />
                    <Column
                      header="Used wgt."
                      body={(r) => formatAmountWithUom(r.used_weight, r.unit_of_measure)}
                      style={{ textAlign: "right", width: "6.5rem" }}
                    />
                    <Column
                      header="Reason"
                      body={reasonBody}
                      style={{ minWidth: "10rem", maxWidth: "14rem" }}
                    />
                    <Column
                      header="Context"
                      body={contextBody}
                      style={{ minWidth: "12rem" }}
                    />
                    <Column expander={allowExpansion} style={{ width: "3rem" }} />
                  </DataTable>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
